import TranslationNS from '@/utils/Constants/TranslationNS'

const SPECTATORS = TranslationNS.SPECTATORS

/**
 * « Commandes des spectateurs » (`spectators/orders`): the Orders engine with the spectator audience.
 * Anchors: `spec-tiles` (SpectatorTiles), `sec-spec-tickets` (SpectatorTicketsCard),
 * `sec-orders-products` (OrdersProductsCard), `sec-orders-list` / `orders-status` / `orders-search`
 * (shared OrdersListCard); the table head and the pager are reached through the list card's own
 * classes. The spectator drawer and the selection bar only exist after a click, so they are described
 * as entries of the table step.
 */
export const SPECTATOR_ORDERS_TOUR = {
    id: 'spectator-orders',
    version: 1,
    paths: ['spectators/orders'],
    steps: [
        {
            key: 'head',
            target: '[data-tour="page-head"]',
            placement: 'bottom',
            title: 'Your ticket sales',
            description:
                'Every spectator order is listed here. The line under the title says whether sales are open, how many tickets are sold and how many came in.',
        },
        {
            key: 'tiles',
            target: '[data-tour="spec-tiles"]',
            placement: 'bottom',
            title: 'The key figures',
            description: 'Confirmed orders, ticket revenue, product revenue and the platform fees, at a glance.',
        },
        {
            key: 'tickets',
            target: '[data-tour="sec-spec-tickets"]',
            placement: 'bottom',
            title: 'Sales per ticket',
            description:
                'For each ticket type: how many sold, the revenue, how many spectators came in and the stock left.',
        },
        {
            key: 'products',
            // the card's title band: one row per product and variant can make the card taller than the screen
            target: '[data-tour="sec-orders-products"]',
            block: ':scope > .org-sec-h',
            placement: 'bottom',
            title: 'Products sold',
            description: 'How many of each extra product were sold with the tickets, and the revenue they brought.',
        },
        {
            key: 'list',
            // the card's title band, not the card: a list taller than the screen leaves no room for the bubble
            target: '[data-tour="sec-orders-list"]',
            block: ':scope > .org-sec-h',
            placement: 'bottom',
            title: 'The list of orders',
            description:
                'One row per order. Click a row to see the tickets, the buyer and the payment, or cancel it. « Export » downloads the list.',
        },
        {
            key: 'status',
            target: '[data-tour="orders-status"]',
            placement: 'bottom',
            title: 'Confirmed or cancelled',
            description: 'These filters show all orders, only the confirmed ones or only the cancelled ones.',
        },
        {
            key: 'search',
            target: '[data-tour="orders-search"]',
            placement: 'bottom',
            title: 'Find an order',
            description:
                'Search by buyer, email or order number, or keep one ticket type only. « Reset filters » shows everything again.',
        },
        {
            key: 'table',
            target: '[data-tour="sec-orders-list"] .org-tbl',
            block: 'thead',
            placement: 'bottom',
            title: 'Rows, columns and selection',
            description:
                'Click a column head to sort. Tick rows to act on several orders at once, click a row to open it.',
            entries: [
                { label: 'Selection', text: 'e-mail the buyers, change the status or export the orders.' },
                { label: 'Order', ns: SPECTATORS, text: 'number, date, status and promo code.' },
                { label: 'Buyer', ns: SPECTATORS, text: 'name, e-mail, phone and delivery address.' },
                {
                    label: 'Tickets',
                    ns: SPECTATORS,
                    text: 'each ticket with its QR code and whether it was scanned at the door.',
                },
                { label: 'Products', ns: SPECTATORS, text: 'the extras ordered.' },
                { label: 'Payment', ns: SPECTATORS, text: 'totals, reduction and the Stripe payment.' },
            ],
        },
        {
            key: 'pager',
            target: '[data-tour="sec-orders-list"] .org-pager',
            placement: 'top',
            title: 'Page by page',
            description:
                'Choose how many rows per page and move between pages. Filters and search apply to the whole list.',
        },
    ],
}

export default SPECTATOR_ORDERS_TOUR
